import React, { useState } from 'react';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Text,
  Badge,
  Button,
  HStack,
  VStack,
  Checkbox,
  Skeleton,
  IconButton,
  Tooltip,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
} from '@chakra-ui/react';

import { FaEye, FaArrowUp, FaArrowDown, FaExternalLinkAlt } from 'react-icons/fa';

import { useColors } from './colors';
import ListingDetailsModal from './ListingDetailsModal';

const RepricerTable = ({
  listings,
  selectedItems,
  setSelectedItems,
  sortBy,
  setSortBy,
  isLoading,
  onAcceptOptimization,
  onAddToQueue,
  onPutOnHold,
  onUnhold,
  onUnlinkCompetitor,
  onBulkUpdate,
  onRejectOptimization,
  storeRules,
  priorityCompetitors = [],
  onLoadListingDetails,
  selectedListingDetails,
  getListingRepricerDetailsStatus,
  isBulkUpdating = false,
  onPageChange,
  currentPage,
  totalPages,
}) => {
  const { borderColor, headerBg, hoverBg, textColor } = useColors();

  // Modal state
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  // Get available bulk actions based on selected listings
  const getAvailableBulkActions = () => {
    if (selectedItems.length === 0) return [];

    const selectedListings = listings.filter((listing) =>
      selectedItems.includes(listing._id),
    );

    const statuses = selectedListings.map(
      (listing) => listing.optimization?.status || 'not-optimized',
    );

    const uniqueStatuses = [...new Set(statuses)];

    // If mixed statuses, don't show any actions
    if (uniqueStatuses.length > 1) return [];

    const status = uniqueStatuses[0];

    switch (status) {
      case 'optimized':
        return [{ value: 'put-on-hold', label: 'Put on Hold' }];
      case 'active':
        return [{ value: 'unlink-competitors', label: 'Unlink Competitors' }];
      case 'queue':
        return [
          { value: 'put-on-hold', label: 'Put on Hold' },
          { value: 'make-active', label: 'Make Active' },
        ];
      case 'hold':
        return [{ value: 'make-unhold', label: 'Make Unhold' }];
      default:
        return [];
    }
  };

  const availableActions = getAvailableBulkActions();

  // Use listings directly from API
  const displayListings = listings || [];

  // Handle bulk update with page navigation logic
  const handleBulkUpdate = async (action) => {
    if (isBulkUpdating) return; // Prevent multiple simultaneous operations

    try {
      await onBulkUpdate(action);

      // After successful bulk update, check if current page is empty
      // and navigate to previous page if needed
      if (currentPage > 1 && displayListings.length === selectedItems.length) {
        // All items on current page were updated, go to previous page
        const newPage = Math.max(1, currentPage - 1);
        if (onPageChange && newPage !== currentPage) {
          onPageChange(newPage);
        }
      }
    } catch (error) {
      console.error('Bulk update failed:', error);
    }
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedItems(displayListings.map((item) => item._id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId, isChecked) => {
    if (isChecked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
    }
  };

  const handleSort = (field) => {
    if (sortBy === field + '+') {
      setSortBy(field + '-');
    } else {
      setSortBy(field + '+');
    }
  };

  const handleViewDetails = async (listing) => {
    setSelectedListing(listing);
    setIsDetailsModalOpen(true);

    // Load detailed listing information with search results
    if (onLoadListingDetails) {
      await onLoadListingDetails(listing._id);
    }
  };

  const handleViewOnMarketplace = (listing) => {
    const marketplace = listing.marketplace?.name?.toLowerCase();
    const externalId = listing.externalPlatformId;

    if (!externalId) {
      console.warn('No external platform ID found for listing:', listing._id);
      return;
    }

    let url;
    if (marketplace === 'ebay') {
      url = `https://www.ebay.com/itm/${externalId}`;
    } else if (marketplace === 'amazon') {
      url = `https://www.amazon.com/dp/${externalId}`;
    } else {
      console.warn('Unsupported marketplace:', marketplace);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedListing(null);
  };

  const handleAcceptOptimizationFromModal = (listingId, recommendedPrice) => {
    onAcceptOptimization(listingId, recommendedPrice);
    handleCloseDetailsModal();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      optimized: { label: 'Optimized', colorScheme: 'green' },
      active: { label: 'Active', colorScheme: 'blue' },
      'not-optimized': { label: 'Not Optimized', colorScheme: 'orange' },
      queue: { label: 'Queue', colorScheme: 'blue' },
      hold: { label: 'Hold', colorScheme: 'gray' },
    };

    const config = statusConfig[status] || { label: status, colorScheme: 'gray' };

    return (
      <Badge
        colorScheme={config.colorScheme}
        variant="subtle"
        px={4}
        py={1}
        borderRadius="md"
      >
        {config.label}
      </Badge>
    );
  };

  const formatPrice = (price) => {
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const getPriceChange = (current, suggested) => {
    if (current == null || current === 0) return { value: 0, percentage: '0.0', isIncrease: false };
    const change = suggested - current;
    const percentage = ((change / current) * 100).toFixed(1);
    return {
      value: change,
      percentage: percentage,
      isIncrease: change > 0,
    };
  };

  const getPriceChangeFromBase = (basePrice, suggestedPrice) => {
    if (basePrice != null && basePrice > 0) {
      return getPriceChange(basePrice, suggestedPrice);
    }
    return null;
  };

  // Get competitor price from backend calculation
  const getCompetitorPrice = (listing) => {
    // Use calculated competitor price from backend if available
    if (listing?.optimization?.calculatedCompetitorPrice) {
      return listing.optimization.calculatedCompetitorPrice;
    }

    // Fallback to existing competitor info
    if (listing?.optimization?.competitorInfo?.price?.value) {
      return parseFloat(listing.optimization.competitorInfo.price.value);
    }

    return 0;
  };

  // Get suggested price from backend calculation
  const getSuggestedPrice = (listing) => {
    // Use calculated suggested price from backend if available
    if (listing?.optimization?.calculatedSuggestedPrice) {
      return listing.optimization.calculatedSuggestedPrice;
    }

    // Fallback to manual calculation
    const competitorPrice = getCompetitorPrice(listing);
    if (competitorPrice === 0) return 0;

    const supplierCost = parseFloat(listing?.supplier?.cost || 0);
    const netMargin = storeRules?.minimumOverallMarkup || 15;
    const marketplaceCommission = storeRules?.marketplaceCommissionMarkup || 13;
    const priceUndercutPercent = storeRules?.priceUndercutPercent || 2;

    // Calculate minimum selling price
    const totalMarkup = netMargin + marketplaceCommission;
    const minimumPrice = supplierCost / ((100 - totalMarkup) / 100);

    // Calculate target price (competitor price - undercut percent)
    const targetPrice = competitorPrice * (1 - priceUndercutPercent / 100);

    // Return the higher of minimum price or target price
    return Math.max(minimumPrice, targetPrice);
  };

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
      bg="white"
      _dark={{ bg: 'navy.800' }}
    >
      {/* Bulk Actions Dropdown */}
      {selectedItems.length > 0 && availableActions.length > 0 && (
        <Box p={4} borderBottom="1px solid" borderColor={borderColor} bg="gray.50">
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color={textColor} fontWeight="medium">
              {selectedItems.length} listing{selectedItems.length > 1 ? 's' : ''} selected
            </Text>
            <HStack spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Actions:
              </Text>
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  isDisabled={isBulkUpdating}
                >
                  {isBulkUpdating ? (
                    <HStack spacing={2}>
                      <Spinner size="xs" />
                      <Text>Processing...</Text>
                    </HStack>
                  ) : (
                    'Select Action'
                  )}
                </MenuButton>
                <MenuList>
                  {availableActions.map((action) => (
                    <MenuItem
                      key={action.value}
                      onClick={() => handleBulkUpdate(action.value)}
                      isDisabled={isBulkUpdating}
                    >
                      {action.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </HStack>
          </Flex>
        </Box>
      )}

      <Box
        maxH="800px" // Max height for ~20 rows (40px per row)
        overflowY="auto"
        overflowX="auto"
        position="relative"
      >
        {/* Loading overlay for bulk operations */}
        {isBulkUpdating && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(255, 255, 255, 0.8)"
            _dark={{ bg: 'rgba(0, 0, 0, 0.8)' }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={10}
            borderRadius="md"
          >
            <VStack spacing={4}>
              <Spinner size="lg" color="blue.500" />
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Processing {selectedItems.length} listings...
              </Text>
            </VStack>
          </Box>
        )}

        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              <Th>
                <Checkbox
                  isChecked={
                    selectedItems.length === displayListings.length &&
                    displayListings.length > 0
                  }
                  isIndeterminate={
                    selectedItems.length > 0 &&
                    selectedItems.length < displayListings.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </Th>
              <Th>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('title')}
                  rightIcon={
                    sortBy === 'title+' ? (
                      <FaArrowUp />
                    ) : sortBy === 'title-' ? (
                      <FaArrowDown />
                    ) : null
                  }
                >
                  Product
                </Button>
              </Th>
              <Th>Supplier</Th>
              <Th>Store</Th>
              <Th>Marketplace</Th>
              <Th>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('currentPrice')}
                  rightIcon={
                    sortBy === 'currentPrice+' ? (
                      <FaArrowUp />
                    ) : sortBy === 'currentPrice-' ? (
                      <FaArrowDown />
                    ) : null
                  }
                >
                  Current Price
                </Button>
              </Th>
              <Th>Competitor Price</Th>
              <Th>Base Formula Price</Th>
              <Th>Suggested Price</Th>
              <Th>Synced Status</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  {Array.from({ length: 12 }).map((_, cellIndex) => (
                    <Td key={cellIndex}>
                      <Skeleton height="20px" />
                    </Td>
                  ))}
                </Tr>
              ))
            ) : displayListings.length > 0 ? (
              displayListings.map((listing) => {
                // Get competitor price and suggested price from backend calculations
                const competitorPrice = getCompetitorPrice(listing);
                const suggestedPrice = getSuggestedPrice(listing);

                // Determine status based on optimization.status field
                const listingStatus = listing?.optimization?.status || 'not-optimized';

                const baseFormulaPrice = listing?.optimization?.baseFormulaPrice;
                const priceChangeFromBase = getPriceChangeFromBase(
                  baseFormulaPrice,
                  suggestedPrice,
                );
                const priceChange =
                  priceChangeFromBase ||
                  getPriceChange(listing.details?.price || 0, suggestedPrice);

                const syncedStatus = listing?.optimization?.syncedStatus ?? 'unsynced';

                return (
                  <Tr
                    key={listing._id}
                    _hover={{ bg: hoverBg }}
                    bg={selectedItems.includes(listing._id) ? 'blue.50' : 'transparent'}
                    _dark={{
                      bg: selectedItems.includes(listing._id)
                        ? 'blue.900'
                        : 'transparent',
                    }}
                  >
                    <Td>
                      <Checkbox
                        isChecked={selectedItems.includes(listing._id)}
                        onChange={(e) => handleSelectItem(listing._id, e.target.checked)}
                      />
                    </Td>
                    <Td>
                      <Flex align="center" gap={3}>
                        <Image
                          src={listing.details?.previewImage || '/placeholder-image.jpg'}
                          alt={listing.details?.title || 'N/A'}
                          boxSize="40px"
                          objectFit="contain"
                          borderRadius="md"
                          fallbackSrc="/placeholder-image.jpg"
                        />
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium" color={textColor}>
                            {listing.details?.title || 'N/A'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            SKU: {listing.sku}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {listing.externalPlatformId}
                          </Text>
                        </VStack>
                      </Flex>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{listing.supplier?.name || 'N/A'}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{listing.store?.name || 'N/A'}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="purple" variant="outline">
                        {listing.marketplace?.name || 'N/A'}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium">
                        {formatPrice(listing.details?.price || 0)}
                      </Text>
                    </Td>
                    <Td>
                      {competitorPrice > 0 ? (
                        <Text fontSize="sm">{formatPrice(competitorPrice)}</Text>
                      ) : listingStatus === 'optimized' ? (
                        <Badge colorScheme="gray" variant="outline" fontSize="xs">
                          No Competitor
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          -
                        </Text>
                      )}
                    </Td>
                    <Td>
                      {baseFormulaPrice != null && baseFormulaPrice > 0 ? (
                        <Text fontSize="sm" fontWeight="medium">
                          {formatPrice(baseFormulaPrice)}
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          –
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatPrice(suggestedPrice)}
                        </Text>
                        <HStack spacing={1}>
                          {priceChange.isIncrease ? (
                            <FaArrowUp color="green" size="10px" />
                          ) : (
                            <FaArrowDown color="red" size="10px" />
                          )}
                          <Text
                            fontSize="xs"
                            color={priceChange.isIncrease ? 'green.500' : 'red.500'}
                          >
                            {priceChange.percentage}%
                          </Text>
                        </HStack>
                      </VStack>
                    </Td>
                    <Td>
                      <Tooltip
                        label={
                          syncedStatus === 'synced'
                            ? 'Listing price was synced with optimizer suggested price'
                            : 'Listing price has not been synced with optimizer suggested price'
                        }
                        placement="top"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                        px={3}
                        py={2}
                        borderRadius="md"
                      >
                        <Badge
                          colorScheme={syncedStatus === 'synced' ? 'blue' : 'yellow'}
                          px={4}
                          py={1}
                          borderRadius="md"
                          cursor="help"
                        >
                          {syncedStatus === 'synced' ? 'Synced' : 'Unsynced'}
                        </Badge>
                      </Tooltip>
                    </Td>
                    <Td>{getStatusBadge(listingStatus)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="View Details">
                          <IconButton
                            icon={<FaEye />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleViewDetails(listing)}
                          />
                        </Tooltip>
                        <Tooltip label="View on Marketplace">
                          <IconButton
                            icon={<FaExternalLinkAlt />}
                            size="sm"
                            colorScheme="gray"
                            variant="ghost"
                            onClick={() => handleViewOnMarketplace(listing)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td colSpan={12} textAlign="center" py={8}>
                  <Text color="gray.500" fontSize="sm">
                    No listings found. Try adjusting your filters or check back later.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Listing Details Modal - only use full details when they match the opened row */}
      <ListingDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        listing={
          selectedListingDetails?._id === selectedListing?._id
            ? selectedListingDetails
            : selectedListing
        }
        storeRules={storeRules}
        onAcceptOptimization={handleAcceptOptimizationFromModal}
        onAddToQueue={onAddToQueue}
        onPutOnHold={onPutOnHold}
        onUnhold={onUnhold}
        onUnlinkCompetitor={onUnlinkCompetitor}
        priorityCompetitors={priorityCompetitors}
        isLoading={getListingRepricerDetailsStatus === 'pending'}
      />
    </Box>
  );
};

export default RepricerTable;
